import React, { useState } from 'react';
import { Crop, Upload, FileText, Image as ImageIcon, X, Info } from 'lucide-react';
import { apiService as api } from '@/services/api/mobileApiService';
import { logger } from '@/utils/logger';
import FloorPlanCropModal from './FloorPlanCropModal';
import PdfPagePickerModal from './PdfPagePickerModal';
import { FloorPlanGridMetadata } from '@/utils/gridCoordinates';
import type { Project } from '@/types/index';

/**
 * Drawer-embeddable upload form for a floor plan. Used inline by
 * FloorPlanBottomSheet when its `view` toggles to 'upload'. Has no
 * screen-level chrome (no back button, no full-screen overlay) —
 * the drawer owns those concerns. Self-contained otherwise: file
 * picker, raster crop modal, multi-page PDF picker, and the Cancel /
 * Upload action row are all owned here.
 *
 * Palette: black ink on the yellow drawer panel, matching the rest
 * of the floor-plan-list view it shares chrome with (BottomDrawer
 * tone='yellow'). Primary CTA mirrors the listFooter buttons:
 * black pill + yellow ink.
 */

interface UploadFloorPlanFieldsProps {
    project: Project;
    /** Called after a successful upload — typically the parent reopens
     *  its list view and re-fetches floor plans. */
    onSuccess: () => void;
    onCancel: () => void;
}

/** Detected source format for an uploaded floor-plan file. */
type FloorPlanFormat = 'pdf' | 'svg' | 'raster';

/** Shape of the `/api/floorplans` upload response we care about. */
interface UploadResponse {
    success?: boolean;
    message?: string;
    error?: string;
}

/**
 * Narrow shape for errors that may carry a `name` (e.g. pdf.js
 * `PasswordException`), a `message`, or an axios-style nested
 * `response.data.message`. Avoids `any` while preserving the original
 * runtime reads exactly.
 */
interface UploadError {
    name?: string;
    message?: string;
    response?: { data?: { message?: string } };
}

const cx = (...parts: Array<string | false | undefined>): string =>
    parts.filter(Boolean).join(' ');

function detectFormat(file: File): FloorPlanFormat {
    const name = file.name.toLowerCase();
    if (file.type === 'application/pdf' || name.endsWith('.pdf')) return 'pdf';
    if (file.type === 'image/svg+xml' || name.endsWith('.svg')) return 'svg';
    return 'raster';
}

const UploadFloorPlanFields: React.FC<UploadFloorPlanFieldsProps> = ({
    project,
    onSuccess,
    onCancel,
}) => {
    const [name, setName] = useState('');
    const [file, setFile] = useState<File | null>(null);
    const [format, setFormat] = useState<FloorPlanFormat>('raster');
    const [croppedBlob, setCroppedBlob] = useState<Blob | null>(null);
    const [gridMetadata, setGridMetadata] = useState<FloorPlanGridMetadata | null>(null);
    const [message, setMessage] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);

    const [showCropModal, setShowCropModal] = useState(false);
    const [showPdfPicker, setShowPdfPicker] = useState(false);
    const [pendingPdf, setPendingPdf] = useState<File | null>(null);

    const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
        if (!e.target.files || !e.target.files[0]) return;
        const selectedFile = e.target.files[0];

        const fmt = detectFormat(selectedFile);

        if (selectedFile.size > 20 * 1024 * 1024) {
            setMessage('File size must be less than 20 MB.');
            return;
        }

        if (fmt === 'raster' && !selectedFile.type.startsWith('image/')) {
            setMessage('Please select an image, PDF or SVG file.');
            return;
        }

        setMessage('');
        setCroppedBlob(null);
        setGridMetadata(null);

        if (fmt === 'pdf') {
            try {
                const pdfjs = await import('pdfjs-dist');
                const workerModule = await import('pdfjs-dist/build/pdf.worker.min.mjs?url');
                pdfjs.GlobalWorkerOptions.workerSrc = workerModule.default;
                const bytes = new Uint8Array(await selectedFile.arrayBuffer());
                const doc = await pdfjs.getDocument({ data: bytes }).promise;
                let numPages: number;
                try {
                    numPages = doc.numPages;
                } finally {
                    await doc.destroy();
                }

                if (numPages === 1) {
                    setFile(selectedFile);
                    setFormat('pdf');
                } else {
                    setPendingPdf(selectedFile);
                    setShowPdfPicker(true);
                }
            } catch (err) {
                const uploadErr = err as UploadError;
                if (uploadErr?.name === 'PasswordException') {
                    setMessage('This PDF is password-protected. Re-export without a password and try again.');
                } else {
                    logger.error('Failed to open selected PDF', { err });
                    setMessage('Could not read this PDF. Try a different file or re-export from your CAD tool.');
                }
            }
            return;
        }

        if (fmt === 'svg') {
            setFile(selectedFile);
            setFormat('svg');
            return;
        }

        // Raster: existing flow — crop modal opens automatically
        setFile(selectedFile);
        setFormat('raster');
        setShowCropModal(true);
    };

    const handlePdfPageConfirm = (blob: Blob, pageNumber: number, totalPages: number) => {
        if (!pendingPdf) return;
        const baseName = pendingPdf.name.replace(/\.pdf$/i, '');
        const wrapped = new File([blob], `${baseName}-p${pageNumber}.pdf`, { type: 'application/pdf' });
        setFile(wrapped);
        setFormat('pdf');
        setShowPdfPicker(false);
        setPendingPdf(null);
        setMessage(`Page ${pageNumber} of ${totalPages} selected.`);
    };

    const handlePdfPageCancel = () => {
        setShowPdfPicker(false);
        setPendingPdf(null);
    };

    const handleCropConfirm = (blob: Blob, metadata: FloorPlanGridMetadata) => {
        setCroppedBlob(blob);
        setGridMetadata(metadata);
        setShowCropModal(false);
        setMessage('Image cropped. Ready to upload.');
        logger.info('Floor plan cropped', { metadata });
    };

    const handleCropCancel = () => setShowCropModal(false);

    const handleOpenCrop = () => {
        if (file && format === 'raster') setShowCropModal(true);
    };

    const handleClearSelection = () => {
        setFile(null);
        setFormat('raster');
        setCroppedBlob(null);
        setGridMetadata(null);
        setPendingPdf(null);
        setMessage('');
    };

    const handleSubmit = async () => {
        if (!name.trim()) {
            setMessage('Please enter a floor plan name.');
            return;
        }
        const imageToUpload = croppedBlob || file;
        if (!imageToUpload) {
            setMessage('Please select a file.');
            return;
        }

        setIsSubmitting(true);
        setMessage('');

        const formData = new FormData();
        if (croppedBlob && format === 'raster') {
            const croppedFile = new File([croppedBlob], file?.name || 'floor-plan.jpg', {
                type: 'image/jpeg',
            });
            formData.append('image', croppedFile);
        } else if (file) {
            formData.append('image', file);
        }
        formData.append('name', name.trim());
        formData.append('project_id', String(project.id));
        if (gridMetadata) {
            formData.append('grid_metadata', JSON.stringify(gridMetadata));
        }

        try {
            const response = await api.post<UploadResponse>('/api/floorplans', formData);
            if (response.success) {
                onSuccess();
            } else {
                const errorMsg = response.message || response.error || 'Upload failed';
                setMessage(`Error: ${errorMsg}`);
            }
        } catch (error) {
            const uploadErr = error as UploadError;
            logger.error('Error uploading floor plan', { error: uploadErr?.message });
            setMessage(`Upload failed: ${uploadErr?.response?.data?.message || uploadErr?.message || 'Unknown error'}`);
        } finally {
            setIsSubmitting(false);
        }
    };

    const hasFile = !!file;
    const canSubmit = hasFile && !!name.trim() && !isSubmitting;
    const isError = message && !message.includes('cropped') && !message.includes('selected.');

    return (
        <>
            <div className="space-y-3 pb-2">
                {/* Vector-format tip — a vector PDF stays razor-sharp at any
                    zoom in the watermark/minimap, unlike a raster screenshot. */}
                <div
                    role="note"
                    className="flex items-start gap-2 rounded-xl border border-black/30 bg-black/10 px-3 py-2 text-black leading-snug"
                    style={{ fontSize: 'clamp(12px, 3vw, 14px)' }}
                >
                    <Info size={16} className="mt-0.5 flex-shrink-0 text-black/75" />
                    <span>
                        For better quality, upload a vector PDF.
                    </span>
                </div>

                {/* Name */}
                <label className="block">
                    <span
                        className="block text-black font-semibold mb-1"
                        style={{ fontSize: 'clamp(12px, 3vw, 14px)' }}
                    >
                        Floor plan name
                    </span>
                    <input
                        type="text"
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                        placeholder="e.g. Ground Floor, Level 1"
                        className="w-full px-3 bg-yellow-100/80 border border-black/30 text-black placeholder-black/40 focus:bg-white focus:border-black focus:outline-none rounded-xl"
                        style={{
                            minHeight: 'var(--touch-target)',
                            fontSize: 'clamp(14px, 3.8vw, 16px)',
                        }}
                        disabled={isSubmitting}
                        autoFocus
                    />
                </label>

                {/* File picker / preview */}
                {!hasFile ? (
                    <label
                        className="flex flex-col items-center justify-center w-full border-2 border-dashed border-black/35 rounded-xl cursor-pointer bg-black/5 hover:bg-black/10 active:bg-black/15 transition-colors py-6 px-4"
                    >
                        <Upload className="w-9 h-9 mb-2 text-black/70" />
                        <p
                            className="text-black font-semibold"
                            style={{ fontSize: 'clamp(13px, 3.4vw, 15px)' }}
                        >
                            Tap to select file
                        </p>
                        <p
                            className="text-black/65 mt-1 text-center leading-snug"
                            style={{ fontSize: 'clamp(11px, 2.8vw, 12px)' }}
                        >
                            PDF or SVG keeps crisp at any zoom · PNG / JPG also OK, up to 20 MB
                        </p>
                        <input
                            type="file"
                            className="hidden"
                            onChange={handleFileChange}
                            accept="image/*,application/pdf,image/svg+xml,.pdf,.svg"
                            disabled={isSubmitting}
                        />
                    </label>
                ) : (
                    <div className="rounded-xl bg-black/5 border border-black/25 p-3">
                        <div className="flex items-center gap-3">
                            <div className="w-10 h-10 rounded-xl bg-black/10 border border-black/25 flex items-center justify-center flex-shrink-0">
                                {format === 'raster' ? (
                                    <ImageIcon size={18} className="text-black/75" />
                                ) : (
                                    <FileText size={18} className="text-black/75" />
                                )}
                            </div>
                            <div className="flex-1 min-w-0">
                                <div
                                    className="text-black font-semibold truncate"
                                    style={{ fontSize: 'clamp(13px, 3.4vw, 15px)' }}
                                >
                                    {file!.name}
                                </div>
                                <div
                                    className="text-black/65 mt-0.5 leading-tight"
                                    style={{ fontSize: 'clamp(11px, 2.8vw, 12px)' }}
                                >
                                    {format === 'pdf' && 'PDF — vector, crisp at any size'}
                                    {format === 'svg' && 'SVG — vector, crisp at any size'}
                                    {format === 'raster' && croppedBlob &&
                                        `Cropped: ${gridMetadata?.cropRegion?.width?.toFixed(0)} × ${gridMetadata?.cropRegion?.height?.toFixed(0)} px`}
                                    {format === 'raster' && !croppedBlob && 'Original image (no crop)'}
                                </div>
                            </div>
                            <button
                                type="button"
                                onClick={handleClearSelection}
                                disabled={isSubmitting}
                                aria-label="Clear file"
                                className="flex items-center justify-center rounded-xl bg-black/10 text-black hover:bg-black/20 active:bg-black/25 transition-colors flex-shrink-0"
                                style={{ width: 'clamp(34px, 9vw, 38px)', height: 'clamp(34px, 9vw, 38px)' }}
                            >
                                <X size={16} />
                            </button>
                        </div>
                        {format === 'raster' && (
                            <button
                                type="button"
                                onClick={handleOpenCrop}
                                disabled={isSubmitting}
                                className="mt-2 w-full flex items-center justify-center gap-2 px-3 rounded-xl border border-black/30 bg-black/5 text-black hover:bg-black/10 active:bg-black/15 font-bold transition-colors disabled:opacity-50"
                                style={{
                                    minHeight: 'clamp(38px, 10vw, 44px)',
                                    fontSize: 'clamp(12px, 3.2vw, 14px)',
                                }}
                            >
                                <Crop size={16} />
                                {croppedBlob ? 'Edit crop' : 'Crop image'}
                            </button>
                        )}
                    </div>
                )}

                {/* Status — dark green / red ink on yellow panel */}
                {message && (
                    <div
                        role={isError ? 'alert' : 'status'}
                        className={cx(
                            'rounded-xl px-3 py-2 leading-snug border',
                            isError
                                ? 'border-red-700/50 bg-red-500/15 text-red-800'
                                : 'border-green-700/50 bg-green-500/15 text-green-800',
                        )}
                        style={{ fontSize: 'clamp(12px, 3vw, 14px)' }}
                    >
                        {message.replace(/^(SUCCESS:|ERROR:)\s*/i, '')}
                    </div>
                )}

                {/* Action row — Cancel = ghost-on-yellow, Upload =
                    dark pill matching the list-view footer buttons. */}
                <div className="flex gap-2 pt-1">
                    <button
                        type="button"
                        onClick={onCancel}
                        disabled={isSubmitting}
                        className="px-4 rounded-xl border border-black/30 bg-black/5 text-black hover:bg-black/10 active:bg-black/15 font-bold transition-colors disabled:opacity-50"
                        style={{
                            minHeight: 'var(--touch-target)',
                            fontSize: 'clamp(13px, 3.4vw, 15px)',
                        }}
                    >
                        Cancel
                    </button>
                    <button
                        type="button"
                        onClick={handleSubmit}
                        disabled={!canSubmit}
                        className="flex-1 flex items-center justify-center gap-2 rounded-xl border-2 border-black bg-black text-yellow-300 hover:bg-black/90 active:scale-[0.98] disabled:opacity-50 disabled:cursor-not-allowed font-bold transition-all"
                        style={{
                            minHeight: 'var(--touch-target)',
                            fontSize: 'clamp(13px, 3.6vw, 15px)',
                        }}
                    >
                        {isSubmitting ? (
                            <>
                                <span className="w-4 h-4 border-2 border-yellow-300/30 border-t-yellow-300 rounded-full animate-spin" />
                                Uploading…
                            </>
                        ) : (
                            <>
                                <Upload size={16} />
                                Upload floor plan
                            </>
                        )}
                    </button>
                </div>
            </div>

            {/* Crop Modal — raster only. Z-[400] sits above the drawer. */}
            {file && format === 'raster' && (
                <FloorPlanCropModal
                    isOpen={showCropModal}
                    imageFile={file}
                    onClose={handleCropCancel}
                    onConfirm={handleCropConfirm}
                />
            )}

            {/* PDF Page Picker — multi-page PDFs only. */}
            {pendingPdf && (
                <PdfPagePickerModal
                    isOpen={showPdfPicker}
                    pdfFile={pendingPdf}
                    onClose={handlePdfPageCancel}
                    onConfirm={handlePdfPageConfirm}
                />
            )}
        </>
    );
};

export default UploadFloorPlanFields;
